import FilterList from '@/components/content/FilterList'
import Input from '@/components/content/Input'
import DateRangePickerInput from '@/components/date/DateRangePickerInput'
import SearchInput from '@/components/system/SearchInput'
import ShipperCodeSelect from '@/components/system/ShipperCodeSelect'
import SelectBox from '@/components/content/SelectBox'
import { type ShipperScope, canSelectShipper, defaultShipper, selectableShippers, shipperScopeOf } from '@/components/system/shipperScope'
import { lockShipperName } from '@/components/system/shipperScopeOptions'
import { InterfaceHistoryParams } from '@/services/history/history.types'
import { DATE_FORMAT } from '@/utils/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

export const defaultValues: InterfaceHistoryParams = {
  // 기본 화주 = 현재 세션 화주 코드(shipper_code — 화주 전환을 반영한다, LOGISPRD-14651). sprNm 은 이름이 아니라 코드다 — 1.0 그리드가 SPR_NM 필터로 거른다.
  sprNm: shipperScopeOf(AuthStorage.user.get()).shipperCode,
  apiDir: '',
  apiNo: '',
  module: '',
  plnDate: [dayjs().format(DATE_FORMAT.dateSimple), dayjs().format(DATE_FORMAT.dateSimple)],
}

/**
 * 범위에 맞춘 기본값 — 잠긴 계정의 화주는 기본 화주([defaultShipper] — 재고통합 대상 계정은 MUSINSA, LOGISPRD-15719)다.
 *  [defaultValues] 는 모듈 로드 시 계산되므로 세션 전환 뒤 진입·초기화도 현재 계정의 화주로 맞춘다.
 */
export const scopedDefaultValues = (scope: ShipperScope): InterfaceHistoryParams =>
  scope.locked ? { ...defaultValues, sprNm: defaultShipper(scope).shipperCode } : defaultValues

interface InterfaceHistoryFormProps {
  scope?: ShipperScope
  onSearch(values: InterfaceHistoryParams): void
}
export const InterfaceHistoryForm = ({ onSearch, scope: providedScope }: InterfaceHistoryFormProps) => {
  const { setValue, watch, reset, handleSubmit } = useFormContext<InterfaceHistoryParams>()
  const values = watch()
  const ownScope = useMemo(() => shipperScopeOf(AuthStorage.user.get()), [])
  const scope = providedScope ?? ownScope

  return (
    <FilterList
      list={[
        [
          {
            label: '화주명',
            value: scope.locked && canSelectShipper(scope) ? (
              <SelectBox<string>
                block
                placeholder={false}
                value={values.sprNm}
                options={selectableShippers(scope).map((s) => ({ value: s.shipperCode, label: s.shipperName }))}
                onChange={(v) => setValue('sprNm', v)}
              />
            ) : scope.locked ? (
              // 화주 잠금(LOGISPRD-14653) — 자기 화주로 고정하고 화주 셀렉트를 그리지 않는다(1.0 FilterBar 규칙)
              <SearchInput value={scope.shipperName} disabled />
            ) : (
              <ShipperCodeSelect value={values.sprNm} onChange={(v) => setValue('sprNm', v)} />
            ),
          },
          {
            label: '실행일자',
            required: true,
            value: (
              <DateRangePickerInput
                value={values.plnDate}
                onChange={(v) => {
                  const [start, end] = v
                  setValue('plnDate', [
                    start ? dayjs(start).format(DATE_FORMAT.dateSimple) : '',
                    end ? dayjs(end).format(DATE_FORMAT.dateSimple) : '',
                  ])
                }}
              />
            ),
          },
          {
            label: '통신방향',
            value: <Input value={values.apiDir} onChange={(v) => setValue('apiDir', v)} />,
          },
        ],
        [
          {
            label: 'API 번호',
            value: <Input value={values.apiNo} onChange={(v) => setValue('apiNo', v)} />,
          },
        ],
      ]}
      onReset={() => reset(lockShipperName(scopedDefaultValues(scope), scope))}
      onSearch={handleSubmit(onSearch)}
    />
  )
}
